import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { DataTableFacetedFilter } from './DataTable/DataTableFacetedFilter';
import { DataTableBooleanFilter } from './DataTable/DataTableBooleanFilter';
import { DataTableTextFilter } from './DataTable/DataTableTextFilter';
import { DataTableDateFilter } from './DataTable/DataTableDateFilter';
import { DataTableDateRangeFilter } from './DataTable/DataTableDateRangeFilter';
import { DataTableNumberRangeFilter } from './DataTable/DataTableNumberRangeFilter';

export type FilterConfig = {
    key: string;
    title: string;
    type: 'multi-select' | 'select' | 'boolean' | 'date' | 'date-range' | 'number-range' | 'text' | 'number';
    options?: { value: string; label: string }[];
    renderOption?: (option: { value: string; label: string }) => ReactNode;
    placeholder?: string;
};

type FilterBarProps = {
    filters: FilterConfig[];
    filterState: Record<string, unknown>;
    updateFilter: (key: string, value: unknown) => void;
    clearFilters: () => void;
    actions?: ReactNode;
    searchValue?: string;
    onSearchChange?: (value: string) => void;
    searchPlaceholder?: string;
    className?: string;
};

export function FilterBar({
    filters,
    filterState,
    updateFilter,
    clearFilters,
    actions,
    searchValue,
    onSearchChange,
    searchPlaceholder = 'Recherche...',
    className,
}: FilterBarProps) {
    const hasActiveFilters = Object.values(filterState).some((v) => v !== undefined);
    const showSearchInput = onSearchChange !== undefined;

    function renderFilter(config: FilterConfig) {
        const value = filterState[config.key];

        switch (config.type) {
            case 'select':
                return (
                    <DataTableFacetedFilter
                        key={config.key}
                        title={config.title}
                        options={config.options || []}
                        selectedValues={Array.isArray(value) ? value : value ? [String(value)] : []}
                        onChange={(values) => updateFilter(config.key, values.length > 0 ? values : undefined)}
                        renderOption={config.renderOption}
                    />
                );
            case 'multi-select':
                return (
                    <DataTableFacetedFilter
                        key={config.key}
                        title={config.title}
                        options={config.options || []}
                        selectedValues={Array.isArray(value) ? value : []}
                        onChange={(values) => updateFilter(config.key, values.length > 0 ? values : undefined)}
                        multi
                        renderOption={config.renderOption}
                    />
                );
            case 'boolean':
                return (
                    <DataTableBooleanFilter
                        key={config.key}
                        title={config.title}
                        value={value as boolean | undefined}
                        onChange={(v) => updateFilter(config.key, v)}
                    />
                );
            case 'date':
                return (
                    <DataTableDateFilter
                        key={config.key}
                        title={config.title}
                        value={(value as string) || ''}
                        onChange={(v) => updateFilter(config.key, v || undefined)}
                    />
                );
            case 'date-range':
                return (
                    <DataTableDateRangeFilter
                        key={config.key}
                        title={config.title}
                        value={value as { gte?: number; lte?: number } | undefined}
                        onChange={(v) => updateFilter(config.key, v)}
                    />
                );
            case 'number-range':
                return (
                    <DataTableNumberRangeFilter
                        key={config.key}
                        title={config.title}
                        value={value as { gte?: number; lte?: number } | undefined}
                        onChange={(v) => updateFilter(config.key, v)}
                    />
                );
            case 'text':
            case 'number':
                return (
                    <DataTableTextFilter
                        key={config.key}
                        title={config.title}
                        value={(value as string) || ''}
                        onChange={(v) => updateFilter(config.key, v || undefined)}
                        placeholder={config.placeholder}
                        type={config.type === 'number' ? 'number' : 'text'}
                    />
                );
            default:
                return null;
        }
    }

    return (
        <div className={cn('flex flex-col gap-2 px-4 py-3', className)}>
            {showSearchInput && (
                <Input
                    placeholder={searchPlaceholder}
                    value={searchValue ?? ''}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="h-8 w-full"
                    autoFocus
                />
            )}

            <div className="flex flex-wrap items-center gap-2">
                {filters.map(renderFilter)}

                {hasActiveFilters && (
                    <Button type="button" variant="ghost" size="sm" className="h-8" onClick={clearFilters}>
                        Effacer
                        <X className="ml-1 size-3.5" />
                    </Button>
                )}

                {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
            </div>
        </div>
    );
}
